import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, Award, Check, ExternalLink } from 'lucide-react';
import SEO from '../components/SEO';
import CourseSidebar from '../components/sidebars/CourseSidebar';
import AdSlot from '../components/AdSlot';
import { Button } from '../components/ui/button';
import coursesData from '../data/courses.json';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const CourseDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const course = coursesData.find((c: any) => c.slug === slug);

  if (!course) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Course Not Found</h1>
        <Link to="/courses">
          <Button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white">
            Back to Courses
          </Button>
        </Link>
      </div>
    );
  }

  const courseData = course as any;

  return (
    <>
      <SEO
  title={course.title}
  description={course.description}
  schemaType="course"
/>

<BreadcrumbSchema
  items={[
    { name: "Home", url: "https://www.thecodebazaar.com/" },
    { name: "course", url: "https://www.thecodebazaar.com/courses" },
    {
      name: course.title,
      url: `https://www.thecodebazaar.com/courses/${course.slug}`,
    },
  ]}
/>

      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col md:grid lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <CourseSidebar />
          </div>

          <div className="lg:col-span-3">
            {/* Header */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm mb-8">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6">
                <div className="mb-4 md:mb-0">
                  <h1 className="text-4xl font-bold text-gray-900 mb-3">{courseData.title}</h1>
                  <p className="text-lg text-gray-600 mb-4">{courseData.description}</p>
                  
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-sky-500" />
                      <span>{courseData.duration}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Award className="w-4 h-4 text-sky-500" />
                      <span>{courseData.level}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold text-sky-500 mb-2">${courseData.price}</div>
                  {courseData.featured && (
                    <span className="inline-block px-4 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full font-medium">
                      Bestseller
                    </span>
                  )}
                </div>
              </div>

              {courseData.enrollUrl && (
                <a
                  href={courseData.enrollUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white hover:shadow-lg transition-all py-6 text-lg">
                    Enroll Now - ${courseData.price}
                    <ExternalLink className="ml-2 w-5 h-5" />
                  </Button>
                </a>
              )}
            </div>

            {/* Course Image */}
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm mb-8">
              <img
                src={courseData.thumbnail}
                alt={courseData.title}
                className="w-full h-96 object-cover"
              />
            </div>

            {/* Modules */}
            {courseData.modules && courseData.modules.length > 0 && (
              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Course Modules</h2>
                <div className="space-y-3">
                  {courseData.modules.map((module: string, index: number) => (
                    <div
                      key={index}
                      className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-sky-50 transition-colors"
                    >
                      <div className="w-8 h-8 bg-gradient-to-br from-sky-500 to-blue-600 text-white rounded-lg flex items-center justify-center font-semibold flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{module}</h3>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Features */}
            {courseData.features && courseData.features.length > 0 && (
              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">What's Included</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {courseData.features.map((feature: string, index: number) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Custom HTML */}
            {courseData.customHtml && (
              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm mb-8">
                <div
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: courseData.customHtml }}
                />
              </div>
            )}

            <AdSlot slot="heroBanner" className="mb-10" />


            {/* Final CTA */}
            <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-2xl p-8 border border-sky-200 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Ready to Start Learning?
              </h3>
              <p className="text-gray-600 mb-6">
                Join thousands of students already enrolled in this course
              </p>
              {courseData.enrollUrl && (
                <a
                  href={courseData.enrollUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white hover:shadow-lg transition-all px-8 py-6 text-lg">
                    Enroll Now
                    <ExternalLink className="ml-2 w-5 h-5" />
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CourseDetail;